import React from 'react';

import type { InsightExpertPayload } from '~/components/AiChat/types';
import { formatConfidence } from '~/components/ClaimPage/CliveApp/Confidence';
import { CliveAvatar } from '~/components/Clive/CliveAvatar';
import ShowAllCollapsible from '~/components/core/Molecules/Collapsible/ShowAllCollapsible';
import { Text } from '~/components/core/TextComponents';

/**
 * Pink panel surfacing the expert payload's confidence for an insight. Shows the confidence
 * level (or raw score as a fallback) and the explanation, with the explanation collapsed via
 * the core ShowAllCollapsible "show more" control.
 */
export const ConfidenceSection: React.FC<{ payload: InsightExpertPayload }> = ({ payload }) => {
  const { confidence_level, confidence_score, confidence_explanation } = payload;
  const confidenceValue = formatConfidence({ level: confidence_level, score: confidence_score });

  return (
    <div className="flex flex-col gap-8 rounded-xl bg-pink-100 p-16">
      <div className="flex items-center gap-8">
        <CliveAvatar mood={CliveAvatar.MOOD.HAPPY} size={20} className="shrink-0" />
        <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.BLACK}>
          Confidence
        </Text>
        {confidenceValue ? (
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} colorVariant={Text.COLOR_VARIANTS.PRIMARY}>
            {confidenceValue}
          </Text>
        ) : null}
      </div>
      {confidence_explanation ? (
        <ShowAllCollapsible
          visibleLines={3}
          hideChevron
          linkClassName="text-pink-600"
          align="center"
          expandLabel="Show More"
          collapseLabel="Show Less"
        >
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
            {confidence_explanation}
          </Text>
        </ShowAllCollapsible>
      ) : null}
    </div>
  );
};
